import * as THREE from 'three';
import { box, cyl, mat, texMat } from '../materials';
import { woodTexture } from '../textures';

/**
 * Props that get carried out for a game between players: an old cable spool for a
 * table, two buckets to sit on, and a pair of dice.
 */

/** A wooden cable spool on its side: the trailer-park card table. Two buckets face each other across it (local x). */
export function spoolTable(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#b0875a' });
  g.add(cyl(0.48, 0.48, 0.06, wood, 0, 0.6, 0, 18));
  g.add(cyl(0.48, 0.48, 0.06, wood, 0, 0.03, 0, 18));
  g.add(cyl(0.2, 0.2, 0.52, mat('#8a6a48'), 0, 0.31, 0, 12));
  for (const s of [-1, 1]) {
    const bucket = cyl(
      0.17,
      0.14,
      0.42,
      mat(s < 0 ? '#e8642a' : '#f2f2ee', { rough: 0.6 }),
      s * 0.78,
      0.21,
      0,
      12,
    );
    g.add(bucket);
    g.add(box(0.3, 0.02, 0.06, mat('#555'), s * 0.78, 0.43, 0));
  }
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** Pip layouts on a 3x3 grid (0..8, row-major). */
const PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const faceCache = new Map<string, THREE.MeshStandardMaterial>();
function faceMaterial(n: number, color: string, pip: string): THREE.MeshStandardMaterial {
  const key = `${n}|${color}|${pip}`;
  let m = faceCache.get(key);
  if (m) return m;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = color;
    g.fillRect(0, 0, 64, 64);
    g.fillStyle = pip;
    for (const i of PIPS[n] ?? []) {
      g.beginPath();
      g.arc(14 + (i % 3) * 18, 14 + Math.floor(i / 3) * 18, 6, 0, Math.PI * 2);
      g.fill();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  m = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.4 });
  faceCache.set(key, m);
  return m;
}

/** Which number is on each box face (+x, -x, +y, -y, +z, -z); opposite faces add to 7. */
const FACES = [2, 5, 1, 6, 3, 4];

/** A die, 7 cm on a side. */
export function die(color = '#f4f1e8', pip = '#1b1b1b'): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.07, 0.07),
    FACES.map((n) => faceMaterial(n, color, pip)),
  );
  m.castShadow = true;
  m.userData.noBatch = true;
  return m;
}

/** The rotation that puts `n` on top. */
export function dieFaceUp(n: number, out = new THREE.Quaternion()): THREE.Quaternion {
  const e = new THREE.Euler();
  switch (n) {
    case 2:
      e.set(0, 0, Math.PI / 2);
      break;
    case 5:
      e.set(0, 0, -Math.PI / 2);
      break;
    case 3:
      e.set(-Math.PI / 2, 0, 0);
      break;
    case 4:
      e.set(Math.PI / 2, 0, 0);
      break;
    case 6:
      e.set(Math.PI, 0, 0);
      break;
    default:
      break;
  }
  return out.setFromEuler(e);
}
